/**
 * Document Picture-in-Picture lifecycle hook.
 *
 * Wraps `window.documentPictureInPicture.requestWindow(...)` (Chromium
 * 116+, Electron) so the call surface can mount into a real OS window
 * the user can drag anywhere on screen — including off the Ano window
 * itself, onto a second monitor, etc. The browser-page model can't do
 * that natively; PiP is the only escape hatch the platform offers.
 *
 * Returns a target Document the caller can `createPortal` into, plus
 * an imperative `open / close` pair. When the PiP window closes (user
 * clicks its native close button, navigates the parent page, etc.)
 * the hook flips `pipDocument` back to null so the caller can re-mount
 * the surface inline.
 *
 * Style copying: the PiP window is a fresh `Document` with no
 * stylesheets. We pull every same-origin CSSStyleSheet from the parent
 * page into a `<style>` block, and link cross-origin stylesheets via
 * `<link rel="stylesheet">`. Without this, the LiveKit prebuilt UI
 * lands unstyled in the PiP window.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
  window: Window | null;
}

/** Window augmentation for the documentPictureInPicture API. */
type WindowWithDocPip = Window & {
  documentPictureInPicture?: DocumentPictureInPicture;
};

export function isDocPipSupported(): boolean {
  if (typeof window === "undefined") return false;
  return "documentPictureInPicture" in window;
}

interface UseDocPipResult {
  /** Target Document for `createPortal`, or null when no PiP window is open. */
  pipDocument: Document | null;
  /** True when a PiP window is currently mounted. */
  isOpen: boolean;
  /** Whether the platform supports the API at all. */
  supported: boolean;
  /** Imperatively request a PiP window. Resolves once the window is
   *  ready to portal into. No-op if already open or unsupported. */
  open: (size?: { width?: number; height?: number }) => Promise<void>;
  /** Imperatively close the PiP window. No-op if none is open. */
  close: () => void;
}

/**
 * Copy the parent page's stylesheets into the PiP window's document so
 * the portaled subtree renders styled. Same-origin sheets are inlined
 * (read via `cssRules` then dumped into a `<style>` block);
 * cross-origin sheets fall back to a `<link>` element with the same
 * href so the PiP window fetches them itself.
 */
function copyStyleSheets(pipDoc: Document) {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join("\n");
      const style = pipDoc.createElement("style");
      style.textContent = rules;
      pipDoc.head.appendChild(style);
    } catch {
      if (sheet.href) {
        const link = pipDoc.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        pipDoc.head.appendChild(link);
      }
    }
  }
}

export function useDocPip(): UseDocPipResult {
  const [pipDocument, setPipDocument] = useState<Document | null>(null);
  // Synchronous gate against rapid double-clicks of the Pop-out
  // button. The state-based check (api.window / pipDocument) reads
  // values that don't update until React re-renders + the
  // requestWindow promise resolves; two clicks in the same tick
  // could otherwise both pass the guard and both call requestWindow,
  // leaving us holding a stale reference to the first window.
  const inFlightRef = useRef(false);

  const supported = isDocPipSupported();

  const close = useCallback(() => {
    const w = (window as WindowWithDocPip).documentPictureInPicture?.window;
    if (w) {
      w.close();
    }
    inFlightRef.current = false;
    setPipDocument(null);
  }, []);

  const open = useCallback(
    async (size?: { width?: number; height?: number }) => {
      const api = (window as WindowWithDocPip).documentPictureInPicture;
      if (!api) return;
      // Already open — no-op. The caller can `close()` first if they
      // want to swap.
      if (inFlightRef.current || api.window || pipDocument) return;
      inFlightRef.current = true;
      try {
        const pipWindow = await api.requestWindow({
          width: size?.width ?? 420,
          height: size?.height ?? 460,
        });
        copyStyleSheets(pipWindow.document);
        // Inherit the page's lang + dir + data-theme so design-system
        // tokens resolve (Ano sets `data-theme` / `data-runtime` on
        // <html>; CSS rules key off them).
        const root = document.documentElement;
        for (const attr of root.getAttributeNames()) {
          const value = root.getAttribute(attr);
          if (value !== null) {
            pipWindow.document.documentElement.setAttribute(attr, value);
          }
        }
        // Inherit body class so theme / scrollbar styling carry over.
        if (document.body.className) {
          pipWindow.document.body.className = document.body.className;
        }
        // The PiP window emits `pagehide` when the user closes it via
        // the native chrome. Reflect that back to React state, and
        // clear the in-flight gate so a future open() can proceed.
        pipWindow.addEventListener("pagehide", () => {
          inFlightRef.current = false;
          setPipDocument(null);
        });
        setPipDocument(pipWindow.document);
      } catch {
        // requestWindow throws if the user denies the prompt or if
        // the platform decides not to honor the call (e.g. fullscreen
        // already active). Caller treats null pipDocument as "stay
        // inline" which is the right fallback.
        inFlightRef.current = false;
      }
    },
    [pipDocument],
  );

  // On unmount, also close any lingering PiP window so we don't leak
  // a detached call surface.
  useEffect(() => {
    return () => {
      const w = (window as WindowWithDocPip).documentPictureInPicture?.window;
      if (w) w.close();
    };
  }, []);

  return {
    pipDocument,
    isOpen: pipDocument !== null,
    supported,
    open,
    close,
  };
}
